/**
 * Subscribe - the €5 a year membership page
 * -----------------------------------------
 * Three things can be true when you land here, and the page adapts:
 *
 *   1. Already a member    -> shows when it renews, plus a manage button
 *   2. Stripe is set up    -> "Pay €5" sends you to Stripe's secure checkout
 *   3. Stripe is NOT set up -> explains that, and offers the development
 *                              shortcut so the rest of the site can be tested
 *
 * Card details are only ever typed on Stripe's own pages, never on this site.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, CreditCard, ShieldCheck, AlertTriangle, Settings } from 'lucide-react';

import { paymentApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { dateOnly } from '../utils/format';
import Loader from '../components/Loader';
import BackLink from '../components/BackLink';
import { useI18n } from '../hooks/useI18n';

const BENEFITS = [
  'subscribe.perk1',
  'subscribe.perk2',
  'subscribe.perk3',
  'subscribe.perk4',
  'subscribe.perk5',
];

export default function Subscribe() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const isActive = user?.subscription?.status === 'active';

  // On first load: read the payment settings, and finish off a Stripe payment
  // if we have just been sent back from their checkout page.
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const settings = await paymentApi.config();
        if (!cancelled) setConfig(settings);

        const sessionId = searchParams.get('session_id');
        if (sessionId) {
          try {
            await paymentApi.verify(sessionId);
            await refresh();
            if (!cancelled) setNotice(t('subscribe.thanks'));
          } catch (err) {
            if (!cancelled) setError(err.message);
          }
          setSearchParams({}, { replace: true });
        }

        if (searchParams.get('cancelled')) {
          if (!cancelled) setNotice(t('subscribe.cancelled'));
          setSearchParams({}, { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
    // Deliberately runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async () => {
    setBusy(true);
    setError('');
    try {
      const { url } = await paymentApi.checkout();
      window.location.href = url; // hand over to Stripe
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const devActivate = async () => {
    setBusy(true);
    setError('');
    try {
      await paymentApi.devActivate();
      await refresh();
      setNotice(t('subscribe.devOn'));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const { url } = await paymentApi.portal();
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  // ---- The site is free at the moment: nothing to sell, so say so plainly ----
  if (config && !config.subscriptionsRequired) {
    return (
      <div className="container section" style={{ maxWidth: 640 }}>
        <div className="page-header text-center">
          <h1>{t('subscribe.freeTitle')}</h1>
          <p style={{ marginInline: 'auto' }}>
            {t('subscribe.nothingToPay')}
          </p>
        </div>

        <div className="card" style={{ borderColor: 'var(--color-green)', borderWidth: 2 }}>
          <div className="row" style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-sm)' }}>
            <CheckCircle2 size={26} className="text-green" aria-hidden="true" />
            <h3>{t('subscribe.canDoEverything')}</h3>
          </div>

          <div className="stack" style={{ gap: 8, marginBottom: 'var(--space-md)' }}>
            {BENEFITS.map((benefit) => (
              <span key={benefit} className="row" style={{ gap: 10 }}>
                <CheckCircle2 size={18} className="text-green" aria-hidden="true" />
                {t(benefit)}
              </span>
            ))}
          </div>

          <Link to="/chores/new" className="btn btn-primary btn-block">
            {t('subscribe.postAChore')}
          </Link>

          <p className="text-sm text-muted text-center" style={{ marginTop: 'var(--space-sm)' }}>
            {t('subscribe.keepingFree')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container section" style={{ maxWidth: 640 }}>
      <BackLink to="/dashboard" label={t('common.backToMyChores')} />

      <div className="page-header text-center">
        <h1>{t('subscribe.membershipTitle')}</h1>
        <p style={{ marginInline: 'auto' }}>
          {t('subscribe.membershipSubtitle')}
        </p>
      </div>

      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-sm)' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      {/* ---------------- ALREADY A MEMBER ---------------- */}
      {isActive ? (
        <div className="card" style={{ borderColor: 'var(--color-green)', borderWidth: 2 }}>
          <div className="row" style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-sm)' }}>
            <CheckCircle2 size={26} className="text-green" aria-hidden="true" />
            <h3>{t('misc.membershipActive')}</h3>
          </div>

          {user.subscription.currentPeriodEnd && (
            <p className="text-muted">
              {t('subscribe.runsUntil')}{' '}
              <strong>{dateOnly(user.subscription.currentPeriodEnd)}</strong>.
            </p>
          )}

          <div className="stack" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-md)' }}>
            <Link to="/chores/new" className="btn btn-primary btn-block">
              {t('subscribe.postAChore')}
            </Link>

            {config?.enabled && user.subscription.stripeCustomerId && (
              <button type="button" className="btn btn-outline btn-block" onClick={manage} disabled={busy}>
                <Settings size={18} /> {t('subscribe.manageOrCancel')}
              </button>
            )}
          </div>
        </div>
      ) : (
        /* ---------------- NOT A MEMBER YET ---------------- */
        <div className="card" style={{ borderColor: 'var(--color-green)', borderWidth: 2 }}>
          <div className="text-center" style={{ marginBottom: 'var(--space-md)' }}>
            <div className="price-tag">{config ? `€${(config.priceCents / 100).toFixed(0)}` : '€5'}</div>
            <p className="text-muted">{t('subscribe.perYear')}</p>
          </div>

          <div className="stack" style={{ gap: 8, marginBottom: 'var(--space-md)' }}>
            {BENEFITS.map((benefit) => (
              <span key={benefit} className="row" style={{ gap: 10 }}>
                <CheckCircle2 size={18} className="text-green" aria-hidden="true" />
                {t(benefit)}
              </span>
            ))}
          </div>

          {config?.enabled ? (
            <>
              <button type="button" className="btn btn-primary btn-block btn-lg" onClick={startCheckout} disabled={busy}>
                <CreditCard size={20} />
                {busy ? 'Taking you to Stripe...' : `Join for ${config.priceLabel}`}
              </button>

              <p className="row text-sm text-muted" style={{ gap: 6, marginTop: 'var(--space-sm)', justifyContent: 'center' }}>
                <ShieldCheck size={15} aria-hidden="true" />
                {t('subscribe.stripeHandles')}
              </p>
            </>
          ) : (
            <>
              <div className="alert alert-warning">
                <AlertTriangle size={20} aria-hidden="true" />
                <div>
                  <strong>{t('misc.cardsOff')}</strong>
                  <br />
                  <span className="text-sm">
                    To take real payments, add your Stripe keys to <code>backend/.env</code> and
                    restart the backend. Until then you can switch membership on for testing.
                  </span>
                </div>
              </div>

              {config?.devBypassAvailable && (
                <button
                  type="button"
                  className="btn btn-outline btn-block"
                  onClick={devActivate}
                  disabled={busy}
                  style={{ marginTop: 'var(--space-sm)' }}
                >
                  {busy ? 'Switching on...' : 'Switch on membership (development only)'}
                </button>
              )}
            </>
          )}
        </div>
      )}

      <p className="text-center text-sm text-muted" style={{ marginTop: 'var(--space-md)' }}>
        {t('subscribe.studentBefore')}{' '}
        <Link to="/marketplace">{t('subscribe.goFindWork')}</Link>.
      </p>
    </div>
  );
}
